import type { AhTier, AhVenue } from './ahReport';

export function fmtNum(n: number | string): string {
  if (typeof n !== 'number') return n;
  return n.toLocaleString('nl-NL');
}

/**
 * Groups the bare numbers in a time string the Dutch way: "152320u" → "152.320u".
 * Only a plain run of four or more digits is touched — anything else is already
 * as it should read: a grouped number ("6.055u", as the Prijsfavorieten pipeline
 * sends it; reading its groups one by one printed "6.55u") or a padded duration
 * part ("1u 03m").
 */
export function fmtTimeNL(s: string): string {
  return s.replace(/\d+(?:[.,]\d+)*/g, (m) => (/^[1-9]\d{3,}$/.test(m) ? Number(m).toLocaleString('nl-NL') : m));
}

export function formatRelativeNL(iso: string | null | undefined, nowMs: number): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const sec = Math.max(0, Math.round((nowMs - then) / 1000));
  if (sec < 60) return 'zojuist';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min geleden`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} uur geleden`;
  const days = Math.floor(hr / 24);
  return `${days} ${days === 1 ? 'dag' : 'dagen'} geleden`;
}

export const TIER_LABEL: Record<AhTier, string> = { premium: 'Premium', gold: 'Gold', silver: 'Silver', bronze: 'Bronze' };

const AH_GAME_NAMES: Record<string, string> = {
  heineken: 'Quiz',
  snickers: 'Slicing Game',
  lays: 'Match Three',
  coca_cola: 'Basket Runner',
  bud: 'Music Game',
};

export const gameLabel = (venue: { id: string; game: string | null }): string | null => AH_GAME_NAMES[venue.id] ?? venue.game;

const HIDDEN_DETAILS = new Set(['Content views']);
export const visibleDetails = (v: AhVenue): [string, number | string | null][] => Object.entries(v.details ?? {}).filter(([k]) => !HIDDEN_DETAILS.has(k));

export function elapsedDayCount(days: ReadonlyArray<string | Date>): number {
  const now = new Date();
  const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const dayUTC = (v: string | Date) => {
    const d = v instanceof Date ? v : new Date(v);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  };
  return Math.max(1, days.filter((d) => dayUTC(d) <= todayUTC).length);
}

export const gamesCompleted = (v: AhVenue): number | null =>
  v.gamesPlayed != null && v.completionRate != null ? Math.round((v.gamesPlayed * v.completionRate) / 100) : null;

export const nc = (n?: number | null) => (n == null ? '—' : fmtNum(n));
export const pctCell = (n?: number | null) => (n == null ? '—' : `${n}%`);

export const grandTotal = (venues: AhVenue[]) =>
  venues.reduce(
    (a, v) => ({
      practice: a.practice + (v.practiceStarts ?? 0),
      games: a.games + (v.gamesPlayed ?? 0),
      completed: a.completed + (gamesCompleted(v) ?? 0),
      coupons: a.coupons + (v.vouchersGiven ?? 0),
    }),
    { practice: 0, games: 0, completed: 0, coupons: 0 },
  );

export const SECTIONS = [
  { id: 'overview', label: 'Overzicht' },
  { id: 'engagement', label: 'Engagement' },
  { id: 'venues', label: 'Brand venues' },
  { id: 'behaviour', label: 'Gebruikersgedrag' },
];
